import { ParameterType } from "jspsych";

/**
 * Convertit un keyCode en nom de touche (par ex. 13 -> "Enter").
 * Ajoute ou adapte les clés si besoin.
 */
function keyCodeToKeyName(keyCode) {
  const keyMap = {
    13: "Enter",
    32: " ",
    37: "ArrowLeft",
    38: "ArrowUp",
    39: "ArrowRight",
    40: "ArrowDown",
    65: "a",
    66: "b",
    67: "c",
  };
  return keyMap[keyCode] || String(keyCode);
}

/**
 * Définition du plugin (métadonnées) pour jsPsych v7+.
 * https://www.jspsych.org/7.3/plugins/creating-plugins/
 */
const info = {
  name: "poldrack-text",
  version: "1.0",
  parameters: {
    text: {
      type: ParameterType.HTML_STRING,
      pretty_name: "Text",
      default: null,
      description: "Le contenu HTML à afficher (instructions, feedback, etc.)",
    },
    cont_key: {
      type: ParameterType.KEY,
      array: true,
      pretty_name: "Continue key",
      default: [],
      description: "Touche(s) que l’utilisateur doit presser pour continuer.",
    },
    timing_response: {
      type: ParameterType.INT,
      pretty_name: "Timing response",
      default: -1,
      description: "Durée (ms) avant de clore automatiquement le trial.",
    },
    timing_post_trial: {
      type: ParameterType.INT,
      pretty_name: "Timing post trial",
      default: 1000,
      description: "Durée (ms) de la pause après ce trial.",
    },
    trial_id: {
      type: ParameterType.STRING,
      default: "poldrack-text-trial",
      description: "Label optionnel pour identifier ce trial dans les données.",
    },
  },
};

/**
 * Plugin poldrack-text (version ES Module).
 * 
 * - Affiche du texte HTML.
 * - Attend une ou plusieurs touches pour continuer,
 *   ou bien se termine au bout de `timing_response` ms.
 */
class PoldrackTextPlugin {
  static info = info;

  constructor(jsPsych) {
    this.jsPsych = jsPsych;
  }

  trial(display_element, trial) {
    // Valeurs par défaut (au cas où)
    if (typeof trial.timing_response === "undefined") {
      trial.timing_response = -1;
    }
    if (typeof trial.cont_key === "undefined") {
      trial.cont_key = [];
    }
    if (typeof trial.timing_post_trial === "undefined") {
      trial.timing_post_trial = 1000;
    }

    // Injecte le HTML dans l'élément
    display_element.innerHTML = trial.text;

    // Moment de départ (si on gère le clic souris)
    let start_time = null;

    // Fonction de fin de trial
    const endTrial = (info) => {
      // Nettoyage de l'écran
      display_element.innerHTML = "";

      // Annuler l'écoute clavier
      if (typeof keyboardListener !== "undefined") {
        this.jsPsych.pluginAPI.cancelKeyboardResponse(keyboardListener);
      }
      // Annuler l'écoute de clic souris
      display_element.removeEventListener("click", mouse_listener);

      // Calcul de la durée du bloc
      let block_duration = trial.timing_response;
      if (info.rt !== -1) {
        block_duration = info.rt;
      }

      // Données à enregistrer
      const trialdata = {
        text: trial.text,
        rt: info.rt,
        key_press: info.key,
        block_duration: block_duration,
        timing_post_trial: trial.timing_post_trial,
      };

      // Fin du trial
      this.jsPsych.finishTrial(trialdata);
    };

    // Callback si on clique souris
    const mouse_listener = () => {
      if (start_time === null) {
        // Si ce n'est pas initialisé, on le fait maintenant
        start_time = performance.now();
      }
      const rt = performance.now() - start_time;
      endTrial({ key: "mouse", rt: rt });
    };

    // Soit la “touche” attendue est “mouse”
    if (trial.cont_key === "mouse") {
      display_element.addEventListener("click", mouse_listener);
      start_time = performance.now();
    } else {
      // Soit on attend une touche clavier
      var keyboardListener = this.jsPsych.pluginAPI.getKeyboardResponse({
        callback_function: (info) => {
          // On a appuyé sur la touche
          endTrial(info);
        },
        valid_responses: trial.cont_key.map((key) => {
          // gère le cas où cont_key serait un code numérique
          return typeof key === "number" ? keyCodeToKeyName(key) : key;
        }),
        rt_method: "performance",   // ou "date" si tu préfères
        persist: false,
        allow_held_key: false,
      });
    }

    // Timeout auto si timing_response > 0
    if (trial.timing_response > 0) {
      this.jsPsych.pluginAPI.setTimeout(() => {
        endTrial({ key: -1, rt: -1 });
      }, trial.timing_response);
    }
  }
}

export default PoldrackTextPlugin;
